import * as ColorFormat from './ConditionalColorFormat';
import { tooltipClasses } from '@mui/material/Tooltip';

import React, { useEffect, useMemo, useState, useRef, useLayoutEffect } from 'react';
import debounce from 'lodash/debounce';
import * as CrossTab from './CrossTab';
import { TableContainer } from '@mui/material';
import { HtmlTooltip, ContructTooltipBody } from './BuildTableComponents';
import { Data, PopupData } from './crossTabInterfaces';
import Pagination from '@mui/material/Pagination';
import useCombinedColorSchemes from '../../../hooks/useCombinedColorSchemes';
import { useSelector } from 'react-redux';

export const BuildTable = ({
  crossTabData,
  dustbinRows,
  dustbinValues,
  dustbinColumns,
  chartPropData,
  chartProperties,
  propKey,
  fontFamily,
  chartControls,
  graphDimension,
  colorScheme,
  chartArea,
  numberOfColsPerMeasure = 0,
}: any) => {
  const pageSettings = useSelector((state: any) => state.pageSettings);
  const [columnWidths, setColumnWidths] = useState<number[]>([]);
  const [rowHeights, setRowHeights] = useState<number[]>([]);
  const [page, setPage] = useState(1);
  var bgcolor: string = '';
  const tableRef = useRef<HTMLTableElement>(null);
  // const [backgroundColor, setBackgroundColor] = useState("white");
  // const [scheme, setScheme] = useState("peacock");
  const pageSize = 200;

  const allThemes = useCombinedColorSchemes();

  // Function to handle background color changes
  // const handleBackgroundColorChange: ChartColorsProps["onBackgroundColorChange"] = (color: {
  //   schemeName: string;
  //   color: string;
  // }) => {
  //   const { schemeName, color: newBackgroundColor } = color;
  //   console.log("Scheme Name:", schemeName);
  //   console.log("Background Color:", newBackgroundColor);
  //   setScheme(schemeName); // Update scheme
  //   setBackgroundColor(newBackgroundColor); // Update background color state
  // };
  // useEffect(() => {
  //   // Simulate a color change
  //   handleBackgroundColorChange({
  //     schemeName: scheme,
  //     color: backgroundColor, // Use the current backgroundColor
  //   });
  //   setScheme(scheme); // Update scheme
  //   setBackgroundColor(backgroundColor);
  // }, [scheme, backgroundColor]);

  useLayoutEffect(() => {
    // Get the widths of the columns after the table has rendered
    // This is for fixing headers to the left in case of left to right Scroll
    if (tableRef.current) {
      const thElements = tableRef.current.querySelectorAll('th'); // For all <th> elements

      const widths = Array.from(thElements).map((th) => th.getBoundingClientRect().width);
      const heights = Array.from(thElements).map((th) => th.getBoundingClientRect().height);

      setColumnWidths(widths);
      setRowHeights(heights);
    }
  }, [crossTabData]);

  const [popupData, setPopupData] = useState<PopupData>();

  const userClickedCell = chartProperties.properties[propKey].crossTabUserClicked;

  const chartThemes = allThemes.filter((el) => {
    if (!chartControls.properties[propKey].colorScheme || chartArea === 'dashboard') {
      return el.name === colorScheme;
    } else {
      return el.name === chartControls.properties[propKey].colorScheme;
    }
  });

  let isDark = chartThemes.length > 0 ? chartThemes[0].dark || false : false;

  const prevCountRef = useRef();

  useEffect(() => {
    prevCountRef.current = userClickedCell;
  });

  /* Construct cell data to show on popup/tooltip */
  const _mouseEnterHandler = (e: React.MouseEvent<HTMLTableDataCellElement, MouseEvent>) => {
    const target = e.target as HTMLTableCellElement;
    let _compareObj: Data = JSON.parse(target.getAttribute('data-compareobj') || '');
    if (!(Object.keys(_compareObj).length === 0 && _compareObj.constructor === Object)) {
      setPopupData({
        data: _compareObj,
        remove: null,
        style: null,
      });
    }
  };

  const debouncedMouseEnterHandler = useMemo(
    () => debounce(_mouseEnterHandler.bind(this), 300),
    [],
  );

  /*  TODO:: Feature to change color of Row/Column cells on header cell click */
  const _getUserClickedColor = (col: any, rowIndex: number, colIndex: number) => {
    let _className = '';

    if (
      userClickedCell &&
      userClickedCell.id &&
      userClickedCell.compare &&
      col &&
      col.displayData !== undefined &&
      col.displayData !== null &&
      col.displayData !== ''
    ) {
      ////TODO:: Need a generic function to check null. i.e. col.displayData

      let _userCellCompareJSON = {}; //// JSON.parse(userClickedCell.compare);
      let _idArray = userClickedCell.id.split('_');
      let _cellData = crossTabData[_idArray[0]].columnItems[_idArray[1]];
      _userCellCompareJSON = _cellData?.compareObj;

      if (Object.keys(_userCellCompareJSON).length > 0) {
        if (_idArray[2] === 'true') {
          _className = 'UserClickedCellRemainingChildren';

          if (_idArray[0] < dustbinColumns.length + 1) {
            if (colIndex >= dustbinRows.length) {
              return CrossTab.getUserClickedClassNameForColor(
                chartPropData,
                col,
                _userCellCompareJSON,
              );
            }
          } else if (_idArray[1] < dustbinRows.length) {
            if (col.rowSpan > 1) {
              return CrossTab.getUserClickedClassNameForColor(
                chartPropData,
                col,
                _userCellCompareJSON,
              );
            } else {
              if (rowIndex === _idArray[0]) {
                return 'UserClickedCellChildren';
              }
            }
          } else {
            if (colIndex === _idArray[1]) {
              return 'UserClickedCellChildren';
            }
          }
        } else {
          if (dustbinValues.length > 1) {
          } else {
          }
        }
      }
    } else {
    }
    return _className;
  };

  /*  Adding class to both Row & Column headers */
  const _getHeaderClassName = (col: any, rowIndex: number, colIndex: number) => {
    let _header = '';

    _header =
      rowIndex < dustbinColumns.length || rowIndex === 0
        ? 'CrossTabHeader '
        : 'CrossTabLeftColumnHeader';

    if (chartProperties.properties[propKey].chartType === 'table') {
      _header = 'TableHeader';
    }

    return col.displayData
      ? _header + _getUserClickedColor(col, rowIndex, colIndex)
      : 'EmptyHeaderCell';
  };

  const paginateCrossTabData = (crossTabData: any) => {
    const chartColumnLength =
      chartProperties.properties[propKey].chartType === 'crossTab'
        ? chartProperties.properties[propKey].chartAxes[2].fields.length
        : 0;

    // basically header row is the last row of the table headers. here header row means the measure values row
    const headerRow = crossTabData[chartColumnLength];
    const headerColumnRows =
      chartColumnLength > 0 ? crossTabData.slice(0, chartColumnLength) : null;

    const crossTabDataWithoutHeader = crossTabData.slice(
      chartColumnLength > 0 ? chartColumnLength + 1 : 1,
    );

    if (crossTabDataWithoutHeader.length < pageSize) {
      return crossTabData;
    }

    const slicedCrossTabData = crossTabDataWithoutHeader.slice(
      pageSize * page - pageSize,
      pageSize * page,
    );

    // const slicedCrossTabData = crossTabDataWithoutHeader

    for (let colIndex = 0; colIndex < slicedCrossTabData[0]?.columnItems?.length; colIndex++) {
      if (slicedCrossTabData.length > 0 && slicedCrossTabData[0].columnItems[colIndex].skip) {
        slicedCrossTabData[0].columnItems[colIndex].displayData =
          slicedCrossTabData[0].columnItems[colIndex].compareObj[
            headerRow.columnItems[colIndex].displayData
          ];

        slicedCrossTabData[0].columnItems[colIndex].isHeaderField = true;

        slicedCrossTabData[0].columnItems[colIndex].skip = false;

        let fakeRowIndex = 0;
        while (slicedCrossTabData[fakeRowIndex + 1]?.columnItems[colIndex]?.displayData === '') {
          slicedCrossTabData[0].columnItems[colIndex].rowSpan++;
          fakeRowIndex++;
        }
      }
    }

    if (headerColumnRows) {
      return [...headerColumnRows, headerRow, ...slicedCrossTabData];
    } else {
      return [headerRow, ...slicedCrossTabData];
    }
  };

  useEffect(() => {
    setPage(1);
  }, [chartProperties.properties[propKey].chartAxes]);

  /*  Construct table header and cell with data */
  const GetTableContent = (crossTabData: any, col: any, rowIndex: number, colIndex: number) => {
    if (col.isHeaderField && !col.skip) {
      let tdStyle: any = ColorFormat.setCellColor(
        true,
        crossTabData,
        colIndex,
        rowIndex,
        col.displayData,
        chartProperties,
        propKey,
        chartControls,
        chartThemes,
        isDark,
        dustbinRows,
      );

      /// topPosition and leftPosition calculated for fixing headers to top and left in case of Scrolling

      const columnItemPrefix = [];
      let running = 0;

      for (let i = 0; i < crossTabData.length; i++) {
        columnItemPrefix[i] = running;
        running += crossTabData[i]?.columnItems?.length || 0;
      }

      const borderWidth = chartControls.properties[propKey].crossTabStyleOptions.borderWidth;

      let topPosition = 0;

      for (let i = 0; i < rowIndex; i++) {
        const shiftedIndex = i + columnItemPrefix[i];

        const height = rowHeights[shiftedIndex] || 0;

        topPosition += height;
      }

      let leftPosition = columnWidths[colIndex - 1];
      for (let ind = 2; ind <= colIndex; ind++) {
        leftPosition += columnWidths[colIndex - ind];
      }

      return (
        /*  Construct header area */
        <th
          id={rowIndex + '_' + colIndex + '_' + col.isHeaderField}
          className={_getHeaderClassName(col, rowIndex, colIndex)}
          data-compareobj={JSON.stringify(col.compareObj)}
          key={colIndex}
          colSpan={col.columnSpan}
          rowSpan={col.rowSpan}
          style={{
            ...tdStyle,
            fontFamily: fontFamily,
            position: 'sticky', // For fixing headers on scrolling
            minWidth: '80px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign:
              rowIndex < dustbinColumns.length + (dustbinValues.length > 0 ? 1 : 0) ||
              rowIndex === 0
                ? chartControls.properties[propKey].crossTabStyleOptions.columnHeaderTextAlign ||
                  'center'
                : chartControls.properties[propKey].crossTabStyleOptions.rowHeaderTextAlign ||
                  'left',

            //Position Fix for headers to top in case of OverflowY
            top:
              rowIndex < dustbinColumns.length + (dustbinValues.length > 0 ? 1 : 0)
                ? `${topPosition}px`
                : 'auto',

            //Position Fix for headers to left in case of OverflowX
            left:
              colIndex < dustbinRows.length
                ? (leftPosition || 0)
                : 'auto',

            // Ensures headers are above other content when scrolling
            zIndex:
              rowIndex < dustbinColumns.length + (dustbinValues.length > 0 ? 1 : 0) &&
              colIndex < dustbinRows.length
                ? 3
                : rowIndex < dustbinColumns.length + (dustbinValues.length > 0 ? 1 : 0)
                  ? 2
                  : colIndex === 0
                    ? 1
                    : 0,

            // Separate vertical/horizontal borders for headers
            ...(rowIndex === 0 && {
              borderTop: `${borderWidth}px solid rgb(222, 222, 222)`,
            }),
            ...(colIndex === 0 && {
              borderLeft: `${borderWidth}px solid rgb(222, 222, 222)`,
            }),
          }}
        >
          {col.displayData}
        </th>
      );
    } else {
      if (!col.skip) {
        let tdStyle: any = ColorFormat.setCellColor(
          false,
          crossTabData,
          colIndex,
          rowIndex,
          col.displayData,
          chartProperties,
          propKey,
          chartControls,
          chartThemes,
          isDark,
          dustbinRows,
        );
        const props = {
          chartProp: chartControls.properties[propKey],
          ...popupData,
        };
        return (
          /*  Construct table body area */
          <HtmlTooltip
            title={popupData && ContructTooltipBody({ ...props, fontFamily: fontFamily })}
            arrow
            placement="bottom-start"
            followCursor
            enterDelay={200}
            leaveDelay={0}
            key={`tooltip-${rowIndex}-${colIndex}`}
            slotProps={{
              popper: {
                sx: {
                  [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
                    {
                      marginTop: '5px',
                    },
                  [`&.${tooltipClasses.popper}[data-popper-placement*="top"] .${tooltipClasses.tooltip}`]:
                    {
                      marginBottom: '0px',
                    },
                  [`&.${tooltipClasses.popper}[data-popper-placement*="right"] .${tooltipClasses.tooltip}`]:
                    {
                      marginLeft: '0px',
                    },
                  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]:
                    {
                      marginRight: '0px',
                    },
                },
              },
            }}
            disableHoverListener={!col.compareObj || Object.keys(col.compareObj).length === 0}
          >
            <td
              id={rowIndex + '_' + colIndex + '_' + col.isHeaderField}
              className={'CrossTabCell ' + _getUserClickedColor(col, rowIndex, colIndex)}
              key={colIndex}
              style={{
                ...tdStyle,
                ...(chartControls.properties[propKey]?.layoutOptions?.tableLayout !== 'stretched' &&
                  chartControls.properties[propKey]?.layoutOptions?.manualHeight === true && {
                    lineHeight: `${Math.max(chartControls.properties[propKey].crossTabStyleOptions.lineHeight, chartControls.properties[propKey].crossTabCellLabelOptions.fontSize * 1.25 + 3)}px`, ////////////// More condition to be added
                  }),
                fontFamily: fontFamily,
                minWidth: '80px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign:
                  chartControls.properties[propKey].crossTabStyleOptions.cellTextAlign || 'right',
                // width: '100%',

                // Separate borders for first column data cells
                ...(colIndex === 0 && {
                  borderLeft: `${chartControls.properties[propKey].crossTabStyleOptions.borderWidth || 1}px solid rgb(222, 222, 222)`,
                }),
              }}
              colSpan={col.columnSpan}
              rowSpan={col.rowSpan}
              data-compareobj={JSON.stringify(col.compareObj)}
              onMouseEnter={(e) => {
                if (chartControls.properties[propKey].mouseOver.enable) {
                  e.persist();
                  debouncedMouseEnterHandler(e);
                }
              }}
              onMouseLeave={(e: any) => {
                e.persist();
              }}
            >
              {col.displayData}
            </td>
          </HtmlTooltip>
        );
      } else {
        return null;
      }
    }
  };

  let _tableContent: any[] = [];
  /*  Construct cross tab chart table  rows */
  if (crossTabData.length > 0) {
    _tableContent = paginateCrossTabData(crossTabData).map((row: any, rowIndex: number) => {
      let _rowContent = [];
      _rowContent.push(
        row.columnItems.map((col: any, colIndex: number) => {
          return GetTableContent(crossTabData, col, rowIndex, colIndex);
        }),
      );

      return (
        <tr
          className="CrossTabRow"
          style={{
            // lineHeight: chartControls.properties[propKey].crossTabStyleOptions.lineHeight,
            ...(chartControls.properties[propKey]?.layoutOptions?.tableLayout !== 'stretched' &&
            chartControls.properties[propKey]?.layoutOptions?.manualHeight === true
              ? {
                  lineHeight: `${getRowHeight(rowIndex + 1)}px`, ////////////// More condition to be added
                }
              : {
                  lineHeight: 1.25,
                }),
            zIndex: 0,
            fontFamily: fontFamily,
          }}
          key={rowIndex}
        >
          {_rowContent}
        </tr>
      );
    });
  }
  /*  Render table and show popup */

  const pageCount = Math.ceil(
    (crossTabData.length - chartProperties.properties[propKey].chartAxes[2].fields.length - 1) /
      pageSize,
  );

  const DEFAULT_WIDTH = 80;
  const numberOfCols = dustbinColumns.length;

  let totalWidth = 0;
  dustbinRows.forEach((row: any) => {
    totalWidth += getColumnWidth(row.displayname);
  });
  dustbinValues.forEach((valueField: any) => {
    totalWidth += getColumnWidth(valueField.displayname) * numberOfColsPerMeasure;
  });
  if (dustbinValues.length === 0) {
    totalWidth +=
      (chartControls.properties[propKey].crossTabStyleOptions.lineWidth || DEFAULT_WIDTH) *
      numberOfColsPerMeasure;
  }
  const widthPercentage = Math.min(100, (totalWidth / graphDimension.width) * 100);

  function getColumnWidth(displayName: string) {
    if (chartControls.properties[propKey]?.layoutOptions?.customWidth?.[displayName]) {
      return chartControls.properties[propKey]?.layoutOptions?.customWidth?.[displayName];
    }
    if (dustbinRows.length === 0 && dustbinColumns.length > 0 && dustbinValues.length > 0) {
      if (!displayName) {
        return chartControls.properties[propKey]?.layoutOptions?.customWidth?.[
          dustbinColumns[0].displayname
        ];
      }
    }
    if (
      dustbinValues.length === 0 &&
      chartControls.properties[propKey].crossTabStyleOptions.lineWidth
    ) {
      return chartControls.properties[propKey].crossTabStyleOptions.lineWidth;
    }

    return DEFAULT_WIDTH;
  }
  function getRowHeight(displayName: string | number) {
    if (chartControls.properties[propKey]?.layoutOptions?.customHeight?.[displayName]) {
      return Math.max(
        chartControls.properties[propKey]?.layoutOptions?.customHeight?.[displayName],
        chartControls.properties[propKey].crossTabHeaderLabelOptions.fontSize * 1.25 + 3,
      );
    }

    return Math.max(
      chartControls.properties[propKey].crossTabStyleOptions.lineHeight,
      chartControls.properties[propKey].crossTabCellLabelOptions.fontSize * 1.25 + 3,
    );
  }

  return (
    <div
      className="CrossTab"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
      }}
    >
      <TableContainer
        sx={{
          flex: 1, // Takes remaining height
          minHeight: 0,
          // overflow: 'hidden',
          // '&:hover': {
          overflow: 'auto',
          // },
          position: 'relative',
          scrollbarGutter: 'stable',
          // width: '100%',
          // tableLayout: 'fixed',
          // Reserve space for scrollbar to prevent layout shift
          // '&::-webkit-scrollbar': {
          //   width: '12px',
          // },
        }}
        className="CrossTabTableHeader"
      >
        <table
          ref={tableRef}
          className="CrossTabTable"
          style={{
            ...(chartControls.properties[propKey]?.layoutOptions?.tableLayout === 'stretched' && {
              width: '100%',
              height: '100%',
            }),
            ...(chartControls.properties[propKey]?.layoutOptions?.tableLayout !== 'stretched' &&
              chartControls.properties[propKey]?.layoutOptions?.manualWidth === true && {
                tableLayout: 'fixed', ////////////// More condition to be added
                width: `${widthPercentage}%`, //////////////
              }),
            borderCollapse: 'separate',
            borderSpacing: 0,
            fontFamily: fontFamily,
          }}
        >
          {chartControls.properties[propKey]?.layoutOptions?.tableLayout !== 'stretched' &&
            chartControls.properties[propKey]?.layoutOptions?.manualWidth === true && (
              <colgroup>
                {crossTabData[
                  dustbinValues.length === 0 && numberOfCols > 0 ? numberOfCols - 1 : numberOfCols
                ].columnItems.map((col: any, index: number) =>
                  col.columnSpan === 1 ? (
                    <col
                      key={index}
                      style={{
                        width: `${getColumnWidth(col.displayData)}px`,
                        maxWidth: `${getColumnWidth(col.displayData)}px`,
                      }}
                    />
                  ) : (
                    <>
                      <col
                        key={index + '_1'}
                        style={{
                          width: `${getColumnWidth(col.displayData) / 2}px`,
                          maxWidth: `${getColumnWidth(col.displayData) / 2}px`,
                        }}
                      />
                      <col
                        key={index + '_2'}
                        style={{
                          width: `${getColumnWidth(col.displayData) / 2}px`,
                          maxWidth: `${getColumnWidth(col.displayData) / 2}px`,
                        }}
                      />
                    </>
                  ),
                )}
              </colgroup>
            )}
          {_tableContent}
        </table>
      </TableContainer>
      {pageCount > 1 && (
        <div
          style={{
            position: pageSettings.fullScreen ? 'relative' : 'sticky',
            bottom: pageSettings.fullScreen ? '8.5%' : 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '10px',
            fontFamily: fontFamily,
            zIndex: 5,
            backgroundColor:
              chartThemes?.[0]?.background || chartControls.properties[propKey]?.backgroundColor,
            padding: '2px 0',
          }}
        >
          <span style={{ fontSize: '12px', color: isDark ? 'white' : 'black' }}>Pages</span>
          <Pagination
            style={{
              fontSize: '12px',
            }}
            size="small"
            color="primary"
            shape="rounded"
            count={pageCount}
            page={page}
            onChange={(event, value) => setPage(value)}
            sx={{
              '& .MuiPaginationItem-root': {
                color: isDark ? 'white' : 'black',
              },
              '& .MuiPaginationItem-root.Mui-selected, & .MuiPaginationItem-root.Mui-selected:hover':
                {
                  backgroundColor: 'rgb(237, 235, 235)',
                  color: isDark ? 'black' : 'black',
                },
              '& .MuiPaginationItem-root:hover': {
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
              },
            }}
          />
        </div>
      )}
    </div>
  );
};
